import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/axiosInstance';
import Navbar from '../../components/Navbar';

function UserEstablishmentsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userEstablishments, setUserEstablishments] = useState([]);
  const [availableEstablishments, setAvailableEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState('');

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userRes, allEstablishmentsRes] = await Promise.all([
        axiosInstance.get(`/admin/partner_users`),
        axiosInstance.get('/admin/establishments')
      ]);
      
      // Trouver l'utilisateur spécifique
      const currentUser = userRes.data.users.find(u => u.id === parseInt(userId));
      if (!currentUser) {
        throw new Error('Utilisateur non trouvé');
      }
      
      setUser(currentUser);
      setUserEstablishments(currentUser.establishments || []);
      
      // Filtrer les établissements disponibles (non assignés à cet utilisateur)
      const userEstablishmentIds = (currentUser.establishments || []).map(e => e.id);
      const available = allEstablishmentsRes.data.establishments.filter(
        est => !userEstablishmentIds.includes(est.id)
      );
      setAvailableEstablishments(available);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignEstablishment = async () => {
    if (!selectedEstablishment) {
      alert('Veuillez sélectionner un établissement');
      return;
    }

    try {
      await axiosInstance.post(`/admin/establishments/${selectedEstablishment}/assign_user`, {
        user_id: parseInt(userId)
      });
      
      setShowAssignModal(false);
      setSelectedEstablishment('');
      fetchData(); // Rafraîchir les données
      alert('Établissement assigné avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'assignation');
    }
  };

  const handleUnassignEstablishment = async (establishmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cet établissement de cet utilisateur ?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/establishments/${establishmentId}/unassign_user/${userId}`);
      fetchData(); // Rafraîchir les données
      alert('Établissement retiré avec succès');
    } catch (error) {
      console.error('Erreur lors du retrait:', error);
      alert(error.response?.data?.error || 'Erreur lors du retrait');
    }
  };

  const handleGoToEstablishment = (establishmentId) => {
    navigate(`/admin/${establishmentId}`);
  };

  const styles = {
    container: {
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    header: {
      backgroundColor: '#ffffff',
      padding: '32px',
      borderBottom: '1px solid #e2e8f0',
      marginBottom: '32px'
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto'
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px',
      fontSize: '14px',
      color: '#64748b'
    },
    breadcrumbLink: {
      color: '#3b82f6',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s ease-in-out'
    },
    title: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 8px 0'
    },
    subtitle: {
      fontSize: '16px',
      color: '#64748b',
      margin: 0
    },
    content: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 32px'
    },
    userInfo: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '32px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    },
    userInfoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px'
    },
    userInfoItem: {
      display: 'flex',
      flexDirection: 'column'
    },
    userInfoLabel: {
      fontSize: '12px',
      color: '#64748b',
      fontWeight: '500',
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    userInfoValue: {
      fontSize: '16px',
      color: '#1e293b',
      fontWeight: '600'
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500'
    },
    adminBadge: {
      backgroundColor: '#fef3c7',
      color: '#d97706'
    },
    userBadge: {
      backgroundColor: '#dbeafe',
      color: '#2563eb'
    },
    section: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      marginBottom: '32px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    sectionTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0
    },
    button: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    dangerButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 16px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    adminButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 16px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    secondaryButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    establishmentsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
      gap: '20px'
    },
    establishmentCard: {
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '20px',
      backgroundColor: '#ffffff',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    },
    establishmentHeader: {
      marginBottom: '16px'
    },
    establishmentName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      margin: '0 0 8px 0'
    },
    establishmentMeta: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '4px'
    },
    establishmentInfo: {
      fontSize: '12px',
      color: '#64748b',
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    establishmentDetails: {
      marginBottom: '16px',
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    establishmentDetailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
      fontSize: '13px'
    },
    establishmentDetailLabel: {
      color: '#64748b',
      fontWeight: '500'
    },
    establishmentDetailValue: {
      color: '#1e293b',
      fontWeight: '500'
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '500'
    },
    visibleBadge: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    hiddenBadge: {
      backgroundColor: '#fef2f2',
      color: '#dc2626'
    },
    establishmentActions: {
      display: 'flex',
      gap: '8px',
      marginTop: '16px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#64748b'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      opacity: 0.5
    },
    emptyText: {
      fontSize: '18px',
      fontWeight: '500',
      marginBottom: '8px'
    },
    emptySubtext: {
      fontSize: '14px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      width: '100%',
      maxWidth: '500px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '24px',
      textAlign: 'center'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      backgroundColor: '#ffffff',
      marginBottom: '24px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Navbar />
        <div style={{ ...styles.content, textAlign: 'center', paddingTop: '100px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.container}>
        <Navbar />
        <div style={{ ...styles.content, textAlign: 'center', paddingTop: '100px' }}>
          <div style={{ fontSize: '18px', color: '#dc2626' }}>Utilisateur non trouvé</div>
          <button 
            style={styles.button} 
            onClick={() => navigate('/admin/users')}
         
          >
            Retour à la gestion des utilisateurs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Navbar />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.breadcrumb}>
            <span 
              style={styles.breadcrumbLink}
              onClick={() => navigate('/admin/users')}
              onMouseEnter={(e) => e.target.style.color = '#2563eb'}
              onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
            >
              Gestion des utilisateurs
            </span>
            <span>›</span>
            <span>Établissements de {user.username}</span>
          </div>
          <h1 style={styles.title}>Établissements de {user.username}</h1>
          <p style={styles.subtitle}>Gérez les établissements assignés à cet utilisateur</p>
        </div>
      </div>

      <div style={styles.content}>
        {/* Informations utilisateur */}
        <div style={styles.userInfo}>
          <div style={styles.userInfoGrid}>
            <div style={styles.userInfoItem}>
              <div style={styles.userInfoLabel}>Nom d'utilisateur</div>
              <div style={styles.userInfoValue}>{user.username}</div>
            </div>
            <div style={styles.userInfoItem}>
              <div style={styles.userInfoLabel}>Email</div>
              <div style={styles.userInfoValue}>{user.email}</div>
            </div>
            <div style={styles.userInfoItem}>
              <div style={styles.userInfoLabel}>Type de compte</div>
              <div style={styles.userInfoValue}>
                <span style={{
                  ...styles.badge,
                  ...(user.admin ? styles.adminBadge : styles.userBadge)
                }}>
                  {user.admin ? 'Administrateur' : 'Utilisateur'}
                </span>
              </div>
            </div>
            <div style={styles.userInfoItem}>
              <div style={styles.userInfoLabel}>Établissements assignés</div>
              <div style={styles.userInfoValue}>
                {userEstablishments.length} établissement{userEstablishments.length > 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Établissements assignés */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              Établissements assignés ({userEstablishments.length})
            </h2>
            <button
              style={styles.button}
              onClick={() => setShowAssignModal(true)}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
            >
              + Assigner un établissement
            </button>
          </div>

          {userEstablishments.length > 0 ? (
            <div style={styles.establishmentsGrid}>
              {userEstablishments.map((establishment) => (
                <div 
                  key={establishment.id} 
                  style={styles.establishmentCard}
                  onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 12px 0 rgba(0, 0, 0, 0.15)'}
                  onMouseLeave={(e) => e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)'}
                >
                  <div style={styles.establishmentHeader}>
                    <h3 style={styles.establishmentName}>{establishment.name}</h3>
                    <div style={styles.establishmentMeta}>ID: {establishment.id}</div>
                  </div>

                  <div style={styles.establishmentDetails}>
                    <div style={styles.establishmentDetailRow}>
                      <span style={styles.establishmentDetailLabel}>Ville:</span>
                      <span style={styles.establishmentDetailValue}>
                        {establishment.city || 'Non renseignée'}
                      </span>
                    </div>
                    <div style={styles.establishmentDetailRow}>
                      <span style={styles.establishmentDetailLabel}>Email:</span>
                      <span style={styles.establishmentDetailValue}>
                        {establishment.email || 'Non renseigné'}
                      </span>
                    </div>
                    <div style={styles.establishmentDetailRow}>
                      <span style={styles.establishmentDetailLabel}>Téléphone:</span>
                      <span style={styles.establishmentDetailValue}>
                        {establishment.phone || 'Non renseigné'}
                      </span>
                    </div>
                    <div style={styles.establishmentDetailRow}>
                      <span style={styles.establishmentDetailLabel}>Statut:</span>
                      <span style={{
                        ...styles.statusBadge,
                        ...(establishment.visible ? styles.visibleBadge : styles.hiddenBadge)
                      }}>
                        {establishment.visible ? 'Visible' : 'Masqué'}
                      </span>
                    </div>
                  </div>

                  <div style={styles.establishmentActions}>
                    <button
                      style={styles.adminButton}
                      onClick={() => handleGoToEstablishment(establishment.id)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                    >
                      🔧 Administrer
                    </button>
                    <button
                      style={styles.dangerButton}
                      onClick={() => handleUnassignEstablishment(establishment.id)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                    >
                      ❌ Retirer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🏢</div>
              <div style={styles.emptyText}>Aucun établissement assigné</div>
              <div style={styles.emptySubtext}>
                Cet utilisateur n'a accès à aucun établissement pour le moment
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal d'assignation d'établissement */}
      {showAssignModal && (
        <div style={styles.modal} onClick={() => setShowAssignModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Assigner un établissement</h2>
            
            <select
              value={selectedEstablishment}
              onChange={(e) => setSelectedEstablishment(e.target.value)}
              style={styles.select}
            >
              <option value="">Sélectionner un établissement</option>
              {availableEstablishments.map((establishment) => (
                <option key={establishment.id} value={establishment.id}>
                  {establishment.name} - {establishment.city || 'Ville non renseignée'}
                </option>
              ))}
            </select>

            {availableEstablishments.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                color: '#64748b', 
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                Tous les établissements sont déjà assignés à cet utilisateur
              </div>
            )}

            <div style={styles.buttonGroup}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedEstablishment('');
                }}
              >
                Annuler
              </button>
              <button
                style={{
                  ...styles.button,
                  opacity: !selectedEstablishment ? 0.5 : 1,
                  cursor: !selectedEstablishment ? 'not-allowed' : 'pointer'
                }}
                onClick={handleAssignEstablishment}
                disabled={!selectedEstablishment}
              >
                Assigner
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserEstablishmentsPage;